/** external */
import _ from "lodash";
import React, { memo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { TextInput, IconButton } from "@toddle-edu/ds-web";
import { PencilLineOutlined, DeleteOutlined } from "@toddle-edu/ds-icons";
import PropTypes from "prop-types";
import classNames from "classnames";

/** internal */
import { useI18n } from "Hooks";
import { useDebouncedCallback } from "Utils/CustomHooks";
import {
  updateSection,
  updateSectionV2,
} from "Worksheet/modules/WorksheetModules";
import {
  UPDATE_DEBOUNCE_CALL_TIME,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_CREATE,
  MODE_TEACHER_EVALUATE,
  MODE_STUDENT_EVALUATE,
} from "Worksheet/modules/WorksheetConstants";
import {
  updateSectionLabelInCache,
  updateQuestionFieldsInCache,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import DeleteSectionModal from "Worksheet/components/DeleteSectionModal";

/** relative */
import classes from "./SectionHeader.scss";

const styles = {
  textInputRevampedStyle: {
    border: "none",
    padding: "0",
    maxHeight: "32px",
  },
};

/**
 *
 */
const SectionHeader = memo(props => {
  const {
    id,
    mode,
    label,
    updateSection,
    sectionScore = 0,
    sectionMarks = 0,
    isGeneratingAIQuestions,
    numberOfQuestions,
    parentItemId,
    titleFieldId,
    updateSectionV2,
  } = props;

  const { t } = useI18n(["common", "worksheet"]);
  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  const [isInputMode, setIsInputMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionName, setSectionName] = useState("");

  // * Checkking can edit text, for this case always true as this header will be opeing only for teacher create mode
  const canEdit = _.isEqual(mode, MODE_TEACHER_CREATE);

  // * Setting the section name to the label when it is not in input mode
  useEffect(() => {
    if (!isInputMode) {
      setSectionName(label);
    }
  }, [isInputMode, label]);

  const [debouncedUpdateSectionMutation] = useDebouncedCallback(
    ({ id, label }) => {
      // * To prevent the flickering of the section header while updating the section label
      if (isQueriesV2Enabled) {
        updateQuestionFieldsInCache({
          fields: [
            {
              id: titleFieldId,
              value: label,
            },
          ],
          isQueriesV2Enabled,
        });
      } else {
        updateSectionLabelInCache({ id, label });
      }
      if (isMutationsV2Enabled) {
        updateSectionV2({
          questionId: parentItemId,
          fields: {
            id: titleFieldId,
            value: label,
          },
        });
      } else {
        updateSection({ id, label });
      }
    },
    UPDATE_DEBOUNCE_CALL_TIME
  );

  // * Updating first in local state so that it wont show lagging issue
  const updateSectionHeader = e => {
    const newLabel = _.get(e, "target.value", "");
    setSectionName(newLabel);
    debouncedUpdateSectionMutation({ id, label: newLabel });
  };

  const labelClass = classNames(classes.sectionTitle);

  return (
    <div className={classes.sectionHeader}>
      <div className={classes.container}>
        {isInputMode ? (
          <div className={classes.input}>
            <TextInput
              dsVersion="2.0"
              size="medium"
              value={sectionName}
              onChange={updateSectionHeader}
              autoFocus
              onBlur={() => setIsInputMode(false)}
              style={styles.textInputRevampedStyle}
              testId="worksheet-sectionHeader-updateSectionHeader"
              onKeyDown={e => {
                if (e.key === "Enter") {
                  setIsInputMode(false);
                }
              }}
            />
          </div>
        ) : (
          <div className={classes.innerContainer}>
            <h4 className={labelClass}>{label}</h4>
            {canEdit ? (
              <div className={classes.editActionsContainer}>
                <IconButton
                  dsVersion="2.0"
                  size="x-small"
                  type="plain"
                  variant="primary"
                  icon={<PencilLineOutlined />}
                  onClick={() => setIsInputMode(true)}
                  testId="worksheet-sectionHeader-rename-section"
                />
                <IconButton
                  dsVersion="2.0"
                  size="x-small"
                  type="plain"
                  variant="primary"
                  icon={<DeleteOutlined />}
                  disabled={isGeneratingAIQuestions}
                  testId="worksheet-sectionHeader-delete-section"
                  onClick={() => setShowDeleteDialog(prev => !prev)}
                />
              </div>
            ) : null}
          </div>
        )}

        {/* SHOWING DELETE CONFIRMATION MODAL */}
        {showDeleteDialog && (
          <DeleteSectionModal
            id={id}
            label={label}
            mode={mode}
            onClick={() => setShowDeleteDialog(prev => !prev)}
            toggleModal={() => setShowDeleteDialog(prev => !prev)}
            parentItemId={parentItemId}
          />
        )}

        <div className={classes.sectionScore}>
          {_.includes(
            [MODE_STUDENT_EVALUATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
            mode
          ) ? (
            <div>
              <span className="heading-5">{sectionMarks}</span>
              <span className="text-body-2">
                /{t("common:mark_with_number", { count: sectionScore })}
              </span>
            </div>
          ) : (
            <div className={"text-body"}>
              {t("common:question_number", {
                count: numberOfQuestions,
              })}{" "}
              |{" "}
              {t("common:mark_with_number", {
                count: sectionScore,
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const mapActionCreators = { updateSection, updateSectionV2 };

SectionHeader.displayName = "SectionHeader";

SectionHeader.propTypes = {
  parentItemId: PropTypes.string,
  titleFieldId: PropTypes.string,
  updateSectionV2: PropTypes.func,
};

SectionHeader.defaultProps = {
  parentItemId: null,
  titleFieldId: null,
  updateSectionV2: _.noop,
};

export default connect(null, mapActionCreators)(SectionHeader);
